import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getUserId } from '../utils/tracking';
import axios from 'axios';

const TrackingContext = createContext();

export const useTracking = () => useContext(TrackingContext);

export const TrackingProvider = ({ children }) => {
  const location = useLocation();
  const [sessionData, setSessionData] = useState({
    userId: getUserId(),
    pages: [],
    cursorTrail: [],
    userAgent: navigator.userAgent,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    startTime: Date.now(),
    referrer: document.referrer,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    touchSupport: 'ontouchstart' in window,
  });
  const currentPageRef = useRef({ path: location.pathname, startTime: Date.now() });
  const cursorIntervalRef = useRef(null);

  // Track page changes
  useEffect(() => {
    const now = Date.now();
    const previousPage = currentPageRef.current;
    if (previousPage.path !== location.pathname) {
      const duration = now - previousPage.startTime;
      setSessionData(prev => ({
        ...prev,
        pages: [...prev.pages, { path: previousPage.path, duration }]
      }));
      currentPageRef.current = { path: location.pathname, startTime: now };
    }
  }, [location.pathname]);

  // Track cursor trail
  useEffect(() => {
    const handleMouseMove = (e) => {
      setSessionData(prev => ({
        ...prev,
        cursorTrail: [...prev.cursorTrail, { x: e.clientX, y: e.clientY, time: Date.now() }]
      }));
    };
    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Send data on page hidden and reset session on visible (for mobile support)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        const now = Date.now();
        const duration = now - currentPageRef.current.startTime;
        const finalData = {
          ...sessionData,
          pages: [...sessionData.pages, { path: currentPageRef.current.path, duration }],
          endTime: now,
        };
        // Send to backend
        axios.post('/api/v1/data', finalData).catch(console.error);
      } else {
        // Page became visible again, start new session
        setSessionData({
          userId: getUserId(),
          pages: [],
          cursorTrail: [],
          userAgent: navigator.userAgent,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          startTime: Date.now(),
          referrer: document.referrer,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio,
          touchSupport: 'ontouchstart' in window,
        });
        currentPageRef.current = { path: location.pathname, startTime: Date.now() };
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [location.pathname]);

  // Send data on unload (fallback for desktop)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const now = Date.now();
      const duration = now - currentPageRef.current.startTime;
      const finalData = {
        ...sessionData,
        pages: [...sessionData.pages, { path: currentPageRef.current.path, duration }],
        endTime: now,
      };
      // Send to backend
      axios.post('/api/v1/data', finalData).catch(console.error);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sessionData]);

  return (
    <TrackingContext.Provider value={{ sessionData }}>
      {children}
    </TrackingContext.Provider>
  );
};